<template>
  <button class="google-auth-button" @click="handleClick" type="button">
    <img :src="require('@/assets/svg/google-logo.svg')" class="google-icon" alt="Google" />
    <span class="google-button-text">Continue with Google</span>
  </button>
</template>

<script>
import errorHandler from '@/services/errorHandler';
import { toastError } from '@/services/bus';
export default {
  data() {
    return {
      clientId: process.env.GOOGLE_AUTH_CLIENT_ID
    };
  },
  async mounted() {
    this.checkGoogleScript();
  },
  methods: {
    checkGoogleScript() {
      const id = 'google-script';

      if (document.getElementById(id)) {
        if (window.google && window.google.accounts) {
          this.setupGoogleAuth();
        } else {
          document.getElementById(id).remove();
          this.addGoogleScript();
          this.$trackEvent('google.google-script-reloaded');
        }
      } else {
        this.addGoogleScript(id);
      }
    },
    addGoogleScript(id) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.id = id;

      script.onload = () => {
        this.setupGoogleAuth();
      };

      script.onerror = () => {
        document.getElementById(id).remove();
        errorHandler.captureException('onerror: Could not load the Google script');
        this.$trackEvent('google.google-script-failed');
        toastError('Could not load the Google script, try login with email and password');
      };

      document.head.appendChild(script);
    },
    setupGoogleAuth() {
      if (!window.google || !window.google.accounts) {
        errorHandler.captureException('setupGoogleAuth: window.google or window.google.accounts are undefined');
        return;
      }
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: this.handleCredentialResponse,
        auto_select: false,
        locale: 'en',
        use_fedcm_for_prompt: true
      });
    },
    handleClick() {
      this.$trackEvent('login.google-login-button.click', {
        cta: 'google-button'
      });

      if (window.google && window.google.accounts) {
        // Try to use prompt but with error handling to fall back
        try {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed && notification.isNotDisplayed()) {
              // Prompt was blocked, fall back to code flow
              this.useCodeFlow();
            } else if (notification.isSkippedMoment && notification.isSkippedMoment()) {
              // User skipped, try code flow
              this.useCodeFlow();
            }
          });
        } catch (error) {
          // If prompt fails, use code flow
          this.useCodeFlow();
        }
      } else {
        errorHandler.captureException('handleClick: window.google or window.google.accounts are undefined');
        toastError('Could not load the Google script, try login with email and password');
      }
    },
    useCodeFlow() {
      // Fallback to authorization code flow
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: this.clientId,
        scope: 'email profile openid',
        ux_mode: 'popup',
        callback: (response) => {
          if (response.code) {
            // Send code instead of credential
            this.$emit('success', { code: response.code });
          }
        },
        error_callback: (error) => {
          this.$emit('failure', error);
          errorHandler.captureException('Google OAuth code flow error: ' + JSON.stringify(error));
        }
      });
      client.requestCode();
    },
    handleCredentialResponse(response) {
      response.credential ? this.$emit('success', { ...response }) : this.$emit('failure', { ...response });
    }
  }
};
</script>

<style lang="scss" scoped>
.google-auth-button {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  background: var(--transparent-white-5);
  border: 1px solid var(--transparent-white-5);
  border-radius: 8px;

  padding: 0 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .google-icon {
    width: 18px;
    height: 18px;
  }

  .google-button-text {
    font-family: 'Mulish', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: var(--transparent-white-60);
  }
}
</style>
